import React, { Fragment, useState, useEffect } from 'react';

import styled from 'styled-components';
import { AxiosError } from 'axios';

import FieldView from '../../utils/FieldView';
import NoImage from '../../../NoImage.png';
import Tag from '../../utils/Tag';
import ItemActions from '../../ItemActions/ItemActions';
import ActivityAvgRating from '../ActivityAvgRating';
import { Activity } from '../../../types/Activity';
import { ActivityActions } from '../../../infrastructure/constants/activities';
import Switcher from '../../utils/FormWidgets/Switcher';
import { getConfig } from '../../../config';
import { Vendor } from '../../../types/Vendor';
import { useFetchData } from '../../../infrastructure/hooks/useFetchData';
import * as VendorsService from '../../../services/VendorsService';
import { ApiStatus } from '../../../infrastructure/constants/apiStatus';
import Spinner from '../../utils/Spinner';

import ActivityViewEditableField from './ActivityViewEditableField';
import ActivityViewDeepEditableField from './ActivityViewDeepEditableField';
import ActivityViewMedia from './ActivityViewMedia';
import CategorySelector from './CategorySelector';

const StyledAvgRatingWrapper = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledMobileAvgRatingWrapper = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;
const StyledTitle = styled.h2`
  display: flex;
`;

const StyledPartyProContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  p {
    font-size: 18px;
    margin: 0;
  }
`;

type Props = {
  actions: ActivityActions[];
  activity: Activity;
  back(): void;
  isFareHarbor: boolean;
  setCopyAlert: (str: string) => void;
  updateActivity(fields: unknown): Promise<Activity | undefined>;
};

export default ({
  actions,
  activity,
  back,
  isFareHarbor,
  setCopyAlert,
  updateActivity,
}: Props) => {
  const config = getConfig();
  const [image, setImage] = useState(NoImage);
  const [fieldsInEditMode, setFieldsInEditMode] = useState<string[]>([]);
  const [fetchVendorApiStatus, vendor, fetchVendor] = useFetchData<Vendor>();

  useEffect(() => {
    if (activity.media && activity.media[0]) {
      setImage(activity.media[0].url);
    }
  }, [activity.media]);

  const onEditField = key => {
    const newFieldsInEditMode = [...fieldsInEditMode, key];
    setFieldsInEditMode(newFieldsInEditMode);
  };

  const onCancel = key => {
    const newFieldsInEditMode = fieldsInEditMode.filter(field => field !== key);
    setFieldsInEditMode(newFieldsInEditMode);
  };

  const onSave = async (key, value) => {
    try {
      await updateActivity({ [key]: value });
      onCancel(key);
    } catch (e) {
      console.error(e);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    }
  };

  const onSaveCategory = async (categoryId, subCategoryId) => {
    try {
      const updates: Partial<Activity> = { categoryId, subCategoryId };
      if (activity.categoryId === 'dining' && categoryId !== 'dining') {
        updates.vendorDisplayName = '';
      }

      await updateActivity(updates);
      onCancel(categoryId);
      onCancel(subCategoryId);
    } catch (e) {
      console.error(e);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    }
  };

  const onPartyProChange = async (partyPro: boolean) => {
    try {
      const updates: Partial<Activity> = { partyPro };
      await updateActivity(updates);
    } catch (e) {
      console.error(e);
      const error = e as AxiosError;
      alert(
        `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
      );
    }
  };

  useEffect(() => {
    fetchVendor(() => VendorsService.fetchVendorById(activity.vendorId));
  }, [activity.vendorId, fetchVendor]);

  const redirectToAdminMediaPage = () =>
    window.open(
      `${config.batchAdminURL}/experiences/${activity.id}/media`,
      '_blank'
    );

  return (
    <Fragment>
      <div className='media' style={{ overflow: 'visible' }}>
        <div className='media-left'>
          <img
            alt='ActivityPhoto'
            style={{ width: '210px', height: '210px' }}
            src={
              image.startsWith('https://ik.imagekit.io')
                ? `${image}?tr=w-1200`
                : image
            }
            onError={() => setImage(NoImage)}
          />
          <StyledAvgRatingWrapper>
            <ActivityAvgRating activity={activity} />
          </StyledAvgRatingWrapper>
          <div className='popularity-section' style={{ marginTop: '24px' }}>
            <div className='popularity-block'>
              <span className='popularity-value'>{activity.popularity}</span>
              Popularity
            </div>
            <div className='popularity-block'>
              <span className='popularity-value'>
                {activity.bachelorPopularity}
              </span>
              Bachelor popularity
            </div>
            <div className='popularity-block'>
              <span className='popularity-value'>
                {activity.bachelorettePopularity}
              </span>
              Bachelorette Popularity
            </div>
            <div className='popularity-footer'>higher is sorted first</div>
          </div>
        </div>
        <div className='media-body' style={{ overflow: 'visible' }}>
          <StyledTitle className='media-heading activity-title'>
            <ActivityViewEditableField
              style={{ width: '-webkit-fill-available' }}
              activity={activity}
              fieldsInEditMode={fieldsInEditMode}
              name='title'
              type='text'
              onEditField={onEditField}
              onSave={onSave}
              onCancel={onCancel}
            >
              <b>
                {activity.title}{' '}
                {isFareHarbor ? '(Created from FareHarbor)' : ''}
              </b>
            </ActivityViewEditableField>
            {activity.id && (
              <ItemActions actions={actions} setCopyAlert={setCopyAlert} />
            )}
          </StyledTitle>
          <div className='activity-city'>{activity.city}</div>
          {activity.tags ? (
            <FieldView
              name='Tags'
              md={2}
              sm={4}
              xs={2}
              vmd={10}
              vsm={8}
              vxs={10}
              className='mobile-hidden'
            >
              {' '}
              {activity.tags.map(tag => (
                <Tag key={tag} name={tag} back={back} />
              ))}
            </FieldView>
          ) : (
            ''
          )}
          <CategorySelector activity={activity} onSave={onSaveCategory} />

          <StyledPartyProContainer>
            <p>Party Pro</p>
            <Switcher
              checked={activity.partyPro}
              onChange={value => {
                console.log(value);
                onPartyProChange(value);
              }}
              disabled={false}
              id='partyPro'
            />
          </StyledPartyProContainer>

          {activity.categoryId === 'dining' && (
            <FieldView
              name='Restaurant name (Optional)'
              className='mobile-hidden'
              md={12}
              sm={12}
              xs={12}
              vmd={12}
              vsm={12}
              vxs={12}
            >
              <ActivityViewEditableField
                activity={activity}
                fieldsInEditMode={fieldsInEditMode}
                name='vendorDisplayName'
                onEditField={onEditField}
                onSave={onSave}
                onCancel={onCancel}
                type='text'
                maxLength={50}
              >
                {activity.vendorDisplayName}
              </ActivityViewEditableField>
            </FieldView>
          )}

          {vendor && (
            <FieldView
              name='Vendor Name'
              md={2}
              sm={4}
              xs={2}
              vmd={10}
              vsm={8}
              vxs={10}
              className='mobile-hidden'
            >
              {fetchVendorApiStatus === ApiStatus.loading ? (
                <Spinner />
              ) : (
                <a
                  href={`/vendors/${vendor.type}/${activity.vendorId}`}
                  target='_blank'
                  rel='noreferrer'
                >
                  {vendor.name}
                </a>
              )}
            </FieldView>
          )}
          <FieldView
            name='Description'
            md={2}
            sm={4}
            xs={2}
            vmd={10}
            vsm={8}
            vxs={10}
            className='mobile-hidden'
          >
            <div style={{ width: 'fit-content', position: 'relative' }}>
              {
                // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
                // @ts-ignore
                <ActivityViewDeepEditableField
                  activity={activity}
                  fieldsInEditMode={fieldsInEditMode}
                  name='description'
                  onEditField={onEditField}
                  onSave={onSave}
                  onCancel={onCancel}
                />
              }
            </div>
          </FieldView>
          <FieldView
            name='Media'
            md={2}
            sm={4}
            xs={2}
            vmd={10}
            vsm={8}
            vxs={10}
            className='mobile-hidden'
          >
            <ActivityViewMedia
              activity={activity}
              name='media'
              onEditField={redirectToAdminMediaPage}
            />
          </FieldView>
          {activity.cancellationPolicy && (
            <FieldView
              name='Cancellation-policy'
              md={2}
              sm={4}
              xs={2}
              vmd={10}
              vsm={8}
              vxs={10}
              className='mobile-hidden'
            >
              {activity.cancellationPolicy}
            </FieldView>
          )}
          <FieldView
            name='Сommission'
            md={2}
            sm={4}
            xs={2}
            vmd={10}
            vsm={8}
            vxs={10}
            className='mobile-hidden'
          >
            <ActivityViewEditableField
              activity={activity}
              fieldsInEditMode={fieldsInEditMode}
              name='commission'
              onEditField={onEditField}
              onSave={onSave}
              onCancel={onCancel}
              type='number'
              min={0}
              max={100}
            >
              {activity.commission}
            </ActivityViewEditableField>
          </FieldView>
          <div className='activity-mobile'>
            <div>
              Featured: <span>{activity.featured ? 'True' : 'False'}</span>
            </div>
            {activity.duration && (
              <div>
                Duration: <span>{activity.duration}</span>
              </div>
            )}
            {activity.hours && (
              <div>
                Hours: <span>{activity.hours}</span>
              </div>
            )}
            {activity.bachTip && (
              <div>
                Bach Tip: <span>{activity.bachTip}</span>
              </div>
            )}
            {activity.deadline && (
              <div>
                Booking Cutoff: <span>{+activity.deadline / 24}</span>
              </div>
            )}
            {activity.interval && (
              <div>
                Interval: <span>{activity.interval}</span>
              </div>
            )}
          </div>
          <StyledMobileAvgRatingWrapper>
            <ActivityAvgRating activity={activity} />
          </StyledMobileAvgRatingWrapper>
        </div>
      </div>
      <div className='divider' />
      <FieldView
        className='mobile-hidden activity-info-item'
        name='Featured'
        md={2}
        sm={4}
        xs={2}
        vmd={10}
        vsm={8}
        vxs={10}
        title={`${activity.featured}`}
      >
        {activity.featured ? 'True' : 'False'}
      </FieldView>
      {activity.duration && (
        <FieldView
          className='mobile-hidden activity-info-item'
          name='Duration'
          md={2}
          sm={4}
          xs={2}
          vmd={10}
          vsm={8}
          vxs={10}
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          title={activity.duration}
        >
          {activity.duration}
        </FieldView>
      )}
      {activity.hours && (
        <FieldView
          className='mobile-hidden activity-info-item'
          name='Hours'
          md={2}
          sm={4}
          xs={2}
          vmd={10}
          vsm={8}
          vxs={10}
          title={activity.hours}
        >
          {activity.hours}
        </FieldView>
      )}
      {activity.bachTip && (
        <FieldView
          className='mobile-hidden activity-info-item'
          name='Bach Tip'
          md={2}
          sm={4}
          xs={2}
          vmd={10}
          vsm={8}
          vxs={10}
          title={activity.bachTip}
        >
          {activity.bachTip}
        </FieldView>
      )}
      {activity.deadline && (
        <FieldView
          className='mobile-hidden activity-info-item'
          name='Booking Cutoff'
          md={2}
          sm={4}
          xs={2}
          vmd={10}
          vsm={8}
          vxs={10}
          title={`${+activity.deadline / 24} days`}
        >
          {`${+activity.deadline / 24} days`}
        </FieldView>
      )}
      {activity.interval && (
        <FieldView
          className='mobile-hidden activity-info-item'
          name='Interval'
          md={2}
          sm={4}
          xs={2}
          vmd={10}
          vsm={8}
          vxs={10}
          title={`${activity.interval} minute`}
        >
          {`${activity.interval} minute`}
        </FieldView>
      )}
    </Fragment>
  );
};
